import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalculatorOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  InboxOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Divider,
  Modal,
  Space,
  Steps,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;

const TAB_KEYS = [
  "overview",
  "loosePackages",
  "relabeling",
  "costs",
  "checklist",
];

const ShippingGuideModal = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (visible) {
      setActiveTab("overview");
    }
  }, [visible]);

  const currentIndex = TAB_KEYS.indexOf(activeTab);
  const isFirst = currentIndex <= 0;
  const isLast = currentIndex === TAB_KEYS.length - 1;

  const handleNext = () => {
    if (!isLast) {
      setActiveTab(TAB_KEYS[currentIndex + 1]);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setActiveTab(TAB_KEYS[currentIndex - 1]);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {t("guide.stepIndicator", {
                defaultValue: `Step ${currentIndex + 1} of ${TAB_KEYS.length}`,
                current: currentIndex + 1,
                total: TAB_KEYS.length,
              })}
            </Text>
          </div>
          <Space>
            {!isFirst && (
              <Button onClick={handlePrev} icon={<ArrowLeftOutlined />}>
                {t("guide.prev", "Previous")}
              </Button>
            )}
            {!isLast ? (
              <Button type="primary" onClick={handleNext}>
                {t("guide.next", "Next")} <ArrowRightOutlined />
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={onClose}
                icon={<CheckCircleOutlined />}
              >
                {t("guide.gotIt", "Got It")}
              </Button>
            )}
          </Space>
        </div>
      }
      width={880}
      centered
      bodyStyle={{
        height: "600px",
        maxHeight: "calc(82vh - 140px)",
        overflowY: "auto",
        padding: "16px 20px",
      }}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              backgroundColor: "#e6f7ff",
              color: "#1890ff",
              borderRadius: "8px",
              padding: "6px 10px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <InboxOutlined style={{ fontSize: "20px" }} />
          </div>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1f2937" }}>
              {t("guide.title", "Shipping & Packaging Operational Guide")}
            </Title>
            <Text type="secondary" style={{ fontSize: "13px" }}>
              {t(
                "guide.subtitle",
                "Complete handbook on loose packaging, relabeling rules, and carrier dispatch.",
              )}
            </Text>
          </div>
        </div>
      }
    >
      <div
        style={{
          height: "100%",
          paddingRight: "4px",
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginTop: 0 }}
          items={[
            {
              key: "overview",
              label: (
                <span>
                  <InboxOutlined />{" "}
                  {t("guide.tabs.overview", "Process Overview")}
                </span>
              ),
              children: (
                <div style={{ padding: "8px 4px" }}>
                  <Alert
                    message={t(
                      "guide.twoFlowsTitle",
                      "Standard Operational Flow: Pack First, Bundle Later",
                    )}
                    description={t(
                      "guide.twoFlowsDesc",
                      "Warehouse staff now pack items into physical boxes as soon as they arrive at the warehouse. Once packed into loose packages, staff can select any combination of boxes and bundle them into a shipping order for courier dispatch.",
                    )}
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: 20,
                    }}
                  >
                    <Card
                      size="small"
                      title={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Tag color="blue">
                            {t("guide.newBadge", "STANDARD FLOW")}
                          </Tag>
                          <Text strong>
                            {t(
                              "guide.packFirstTitle",
                              "1. Pack First (Loose Packages)",
                            )}
                          </Text>
                        </div>
                      }
                      style={{
                        borderColor: "#91caff",
                        backgroundColor: "#f0f7ff",
                      }}
                    >
                      <p style={{ fontSize: "13px", marginBottom: 8 }}>
                        {t(
                          "guide.packFirstBestFor",
                          "Best for: Daily operations as items arrive at the warehouse.",
                        )}
                      </p>
                      <ol
                        style={{
                          fontSize: "12px",
                          paddingLeft: 18,
                          margin: 0,
                          lineHeight: 1.8,
                        }}
                      >
                        <li>
                          {t(
                            "guide.packFirstSteps.0",
                            "1688 orders physically arrive at the warehouse.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.packFirstSteps.1",
                            "Click Mark as Arrived on the 1688 order.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.packFirstSteps.2",
                            "Pack arrived items into box(es) in the Packing Station (record weight, dimensions, photo).",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.packFirstSteps.3",
                            "Each box is saved as a Loose Package.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.packFirstSteps.4",
                            "Select packed boxes via checkboxes and click Create Shipment from Packages.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.packFirstSteps.5",
                            "Click Calculate Rate & Place Order with Aramex.",
                          )}
                        </li>
                      </ol>
                    </Card>

                    <Card
                      size="small"
                      title={
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Tag color="default">
                            {t("guide.traditionalBadge", "LEGACY FLOW")}
                          </Tag>
                          <Text strong>
                            {t("guide.groupFirstTitle", "2. Group First Flow")}
                          </Text>
                        </div>
                      }
                      style={{ borderColor: "#d9d9d9" }}
                    >
                      <p style={{ fontSize: "13px", marginBottom: 8 }}>
                        {t(
                          "guide.groupFirstBestFor",
                          "Best for: Pre-planned orders that were already grouped.",
                        )}
                      </p>
                      <ol
                        style={{
                          fontSize: "12px",
                          paddingLeft: 18,
                          margin: 0,
                          lineHeight: 1.8,
                        }}
                      >
                        <li>
                          {t(
                            "guide.groupFirstSteps.0",
                            "Select unassigned 1688 orders via checkboxes.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.groupFirstSteps.1",
                            "Click Create Shipping Order to group them first.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.groupFirstSteps.2",
                            "Open the Packing Station inside that shipment.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.groupFirstSteps.3",
                            "Pack all items into boxes and record box photos.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.groupFirstSteps.4",
                            "Proceed to Aramex dispatch once 100% packed.",
                          )}
                        </li>
                      </ol>
                    </Card>
                  </div>

                  <Title level={5} style={{ marginTop: 16, marginBottom: 12 }}>
                    {t(
                      "guide.workflowStepsTitle",
                      "4-Step Standard Lifecycle",
                    )}
                  </Title>
                  <Steps
                    size="small"
                    direction="vertical"
                    items={[
                      {
                        title: t(
                          "guide.step1Title",
                          "Step 1: Mark 1688 Order as Arrived",
                        ),
                        description: t(
                          "guide.step1Desc",
                          "When parcels arrive at warehouse receiving, mark the 1688 order as 'Arrived'. Arrived items immediately become available for packing.",
                        ),
                        status: "finish",
                      },
                      {
                        title: t(
                          "guide.step2Title",
                          "Step 2: Pack Items in Packing Station (Loose Boxes)",
                        ),
                        description: t(
                          "guide.step2Desc",
                          "Enter quantities going into the current box, weigh on a physical scale (kg), measure exact dimensions (L x W x H cm), check size-relabeling warnings if present, snap a photo, and click 'Record Packed Box'.",
                        ),
                        status: "process",
                      },
                      {
                        title: t(
                          "guide.step3Title",
                          "Step 3: Select Loose Package(s) & Create Shipment",
                        ),
                        description: t(
                          "guide.step3Desc",
                          "Under 'Loose Packages (Ready to Bundle)', check the boxes you want to ship together and click 'Create Shipment from Packages'. Split-order guard ensures whole 1688 orders stay intact.",
                        ),
                        status: "wait",
                      },
                      {
                        title: t(
                          "guide.step4Title",
                          "Step 4: Calculate Rate & Dispatch with Aramex",
                        ),
                        description: t(
                          "guide.step4Desc",
                          "On the created Shipping Order card, click 'Calculate Rate' to preview Aramex pricing. Click 'Place Order' to finalize and print the shipping waybill.",
                        ),
                        status: "wait",
                      },
                    ]}
                  />
                </div>
              ),
            },
            {
              key: "loosePackages",
              label: (
                <span>
                  <InboxOutlined />{" "}
                  {t("guide.tabs.loosePackages", "Loose Packages Flow")}
                </span>
              ),
              children: (
                <div style={{ padding: "8px 4px" }}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    {t(
                      "guide.stepByStepGuideTitle",
                      "Step-by-Step Loose Packaging Guide",
                    )}
                  </Title>
                  <Paragraph style={{ fontSize: "13px" }}>
                    {t(
                      "guide.stepByStepDesc",
                      "Loose packages allow you to box arrived items immediately without waiting for every item or order in the shipment to arrive. You can pack Box #1 today, Box #2 tomorrow, and bundle them whenever you are ready.",
                    )}
                  </Paragraph>

                  <div
                    style={{
                      backgroundColor: "#fafafa",
                      border: "1px solid #f0f0f0",
                      borderRadius: "8px",
                      padding: "14px",
                      marginBottom: 16,
                    }}
                  >
                    <Title
                      level={5}
                      style={{
                        margin: "0 0 10px 0",
                        fontSize: "14px",
                        color: "#1890ff",
                      }}
                    >
                      {t("guide.howToPackAndBundleTitle", "How to pack and bundle:")}
                    </Title>
                    <ul
                      style={{
                        fontSize: "13px",
                        lineHeight: 1.8,
                        margin: 0,
                        paddingLeft: 20,
                      }}
                    >
                      <li>
                        {t(
                          "guide.packStep1",
                          "1. Open Wege Order: Expand the order to see the Packing Station.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep2",
                          "2. Enter Box Quantities: In the Pack in this box (Qty) field, specify how many units of each arrived item are going into this physical box.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep3",
                          "3. Confirm Relabeling (if required): If a yellow warning appears, physically swap the size tag and check 'I attached the correct label'.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep4",
                          "4. Measure & Weigh: Enter actual Weight (kg) and dimensions: Length, Width, Height (cm).",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep5",
                          "5. Record Box: Attach a box photo and click 'Record Packed Box'.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep6",
                          "6. View in Loose Packages: The box immediately registers in the Loose Packages (Ready to Bundle) table.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.packStep7",
                          "7. Select & Bundle: Check the box checkboxes you want to ship and click 'Create Shipment from Packages'.",
                        )}
                      </li>
                    </ul>
                  </div>

                  <Alert
                    type="warning"
                    showIcon
                    icon={<SafetyCertificateOutlined />}
                    message={t(
                      "guide.splitOrderSafetyTitle",
                      "The Split-Order Safety Guard",
                    )}
                    description={
                      <div style={{ fontSize: "13px" }}>
                        <p style={{ margin: "4px 0" }}>
                          {t(
                            "guide.splitOrderSafetyDesc1",
                            "If an Alibaba 1688 order's items are packed across multiple boxes (e.g., Box 1 and Box 2), both boxes must be grouped together so the customer order is never split across different courier shipments.",
                          )}
                        </p>
                        <p style={{ margin: "4px 0" }}>
                          {t(
                            "guide.splitOrderSafetyDesc2",
                            "If you select only Box 1 and click Create Shipment from Packages, the system will automatically select Box 2 for you and notify you to confirm and retry.",
                          )}
                        </p>
                      </div>
                    }
                    style={{ marginBottom: 16 }}
                  />
                </div>
              ),
            },
            {
              key: "relabeling",
              label: (
                <span>
                  <WarningOutlined />{" "}
                  {t("guide.tabs.relabeling", "Size Relabeling Rules")}
                </span>
              ),
              children: (
                <div style={{ padding: "8px 4px" }}>
                  <Alert
                    type="error"
                    showIcon
                    message={t(
                      "guide.relabelingCriticalTitle",
                      "CRITICAL: Physical Label Swapping",
                    )}
                    description={t(
                      "guide.relabelingCriticalDesc",
                      "Never check the label confirmation box without physically replacing the size tag on the garment. Checking the box without swapping the physical tag will cause the customer to receive the wrong size!",
                    )}
                    style={{ marginBottom: 16 }}
                  />

                  <Title level={5} style={{ marginTop: 0 }}>
                    {t(
                      "guide.whyLabelWarningsTitle",
                      "Why do items have label warnings?",
                    )}
                  </Title>
                  <Paragraph style={{ fontSize: "13px" }}>
                    {t(
                      "guide.whyLabelWarningsDesc",
                      "Some manufacturers label garments using local or Asian size charts that do not match our storefront sizing (e.g., a garment tagged as L in the factory actually fits like an S for our customers).",
                    )}
                  </Paragraph>

                  <div
                    style={{
                      backgroundColor: "#fffbe6",
                      border: "1px solid #ffe58f",
                      borderRadius: "8px",
                      padding: "14px",
                      marginBottom: 16,
                    }}
                  >
                    <Text
                      strong
                      style={{
                        color: "#d46b08",
                        display: "block",
                        marginBottom: 6,
                      }}
                    >
                      {t("guide.exampleWarningTitle", "Example Warning:")}
                    </Text>
                    <Tag
                      color="warning"
                      style={{ fontSize: "13px", padding: "4px 8px" }}
                    >
                      {t(
                        "guide.exampleWarningText",
                        "⚠️ Please attach a size S label instead of L.",
                      )}
                    </Tag>
                    <Divider style={{ margin: "10px 0" }} />
                    <p style={{ margin: "4px 0", fontSize: "13px" }}>
                      <strong>
                        {t(
                          "guide.staffActionsTitle",
                          "What warehouse staff must do:",
                        )}
                      </strong>
                    </p>
                    <ol
                      style={{
                        fontSize: "13px",
                        margin: 0,
                        paddingLeft: 20,
                        lineHeight: 1.8,
                      }}
                    >
                      <li>
                        {t(
                          "guide.staffAction1",
                          "Locate the garment inside the receiving shipment.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.staffAction2",
                          "Physically remove the factory label (e.g., L) and attach the designated Wege storefront label (e.g., S).",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.staffAction3",
                          "In the Packing Station, set the quantity to pack.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.staffAction4",
                          "Check the confirmation box: 'I attached the correct label'.",
                        )}
                      </li>
                      <li>
                        {t(
                          "guide.staffAction5",
                          "Place the item into the box and record the box.",
                        )}
                      </li>
                    </ol>
                  </div>
                </div>
              ),
            },
            {
              key: "costs",
              label: (
                <span>
                  <CalculatorOutlined />{" "}
                  {t("guide.tabs.costs", "Aramex & Estimated Cost")}
                </span>
              ),
              children: (
                <div style={{ padding: "8px 4px" }}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    {t(
                      "guide.costCalcTitle",
                      "How Estimated Shipping Cost is Calculated",
                    )}
                  </Title>
                  <Paragraph style={{ fontSize: "13px" }}>
                    {t(
                      "guide.costCalcDesc",
                      "Because every packed box records actual weight and exact physical dimensions (L x W x H), the system calculates the volumetric weight vs actual weight to compute the carrier's real estimated shipping cost.",
                    )}
                  </Paragraph>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "16px",
                      marginBottom: 16,
                    }}
                  >
                    <Card
                      size="small"
                      title={t(
                        "guide.costWhereTitle",
                        "Where does the cost appear?",
                      )}
                    >
                      <ul
                        style={{
                          fontSize: "12px",
                          paddingLeft: 18,
                          margin: 0,
                          lineHeight: 1.8,
                        }}
                      >
                        <li>
                          {t(
                            "guide.costWhere1",
                            "Shipment Header: Displayed as Est. Cost: XX.XX AED next to shipment status.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.costWhere2",
                            "Main Orders Table: In the Est. Shipping column, aggregating all shipments for that Wege Order.",
                          )}
                        </li>
                      </ul>
                    </Card>

                    <Card
                      size="small"
                      title={t(
                        "guide.liveRatesTitle",
                        "Calculating Live Rates",
                      )}
                    >
                      <ul
                        style={{
                          fontSize: "12px",
                          paddingLeft: 18,
                          margin: 0,
                          lineHeight: 1.8,
                        }}
                      >
                        <li>
                          {t(
                            "guide.liveRates1",
                            "Click the 'Calculate Rate' button on any unplaced shipment card to query Aramex live.",
                          )}
                        </li>
                        <li>
                          {t(
                            "guide.liveRates2",
                            "The updated estimated shipping cost appears immediately on the shipment card without needing a full page reload.",
                          )}
                        </li>
                      </ul>
                    </Card>
                  </div>

                  <Alert
                    type="info"
                    showIcon
                    message={t(
                      "guide.submissionGateTitle",
                      "Carrier Submission Gate",
                    )}
                    description={t(
                      "guide.submissionGateDesc",
                      "The system will block any carrier submission if a shipment is not 100% packed or if any size label confirmation is missing. All boxes must be entered before the 'Place Order' button becomes active.",
                    )}
                    style={{ marginBottom: 12 }}
                  />

                  <Alert
                    type="success"
                    showIcon
                    message={t(
                      "guide.recalcOnDemandTitle",
                      "Recalculating Rates on Demand",
                    )}
                    description={t(
                      "guide.recalcOnDemandDesc",
                      "If an Aramex quote was not initially available or package details changed, click the 'Calculate Rate' button on the shipment card. The server will re-query Aramex live and update the estimated cost immediately.",
                    )}
                  />
                </div>
              ),
            },
            {
              key: "checklist",
              label: (
                <span>
                  <FileDoneOutlined />{" "}
                  {t("guide.tabs.checklist", "Handover Checklist")}
                </span>
              ),
              children: (
                <div style={{ padding: "8px 4px" }}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    {t(
                      "guide.checklistTitle",
                      "Pre-Courier Handover Checklist",
                    )}
                  </Title>
                  <Paragraph style={{ fontSize: "13px" }}>
                    {t(
                      "guide.checklistDesc",
                      "Before handing any parcel over to the Aramex courier driver, verify each item on this checklist:",
                    )}
                  </Paragraph>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                    }}
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((idx) => {
                      const defaultItems = [
                        "1. 1688 order items show 'Arrived' and were packed via the Packing Station.",
                        "2. Packed quantities in the system 100% match what is physically inside the box.",
                        "3. Every size-label warning was physically swapped on the garments before sealing.",
                        "4. Box dimensions (L, W, H) and weight were weighed on a physical scale, not guessed.",
                        "5. Box confirmation photo is clear and saved in the system.",
                        "6. Packages were selected and bundled into a Shipping Order via 'Create Shipment from Packages'.",
                        "7. Aramex shipping rate was verified and 'Place Order' was submitted.",
                        "8. Aramex waybill label is printed, legible, and securely taped to the exterior of the box.",
                      ];
                      return (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            backgroundColor: "#f9fafb",
                            padding: "10px 14px",
                            borderRadius: "6px",
                            border: "1px solid #e5e7eb",
                          }}
                        >
                          <CheckCircleOutlined
                            style={{ color: "#52c41a", fontSize: "16px" }}
                          />
                          <Text style={{ fontSize: "13px" }}>
                            {t(`guide.checklistItems.${idx}`, defaultItems[idx])}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default ShippingGuideModal;
